import { Plus, Ban, LockOpen, Lock, FileDiff } from "lucide-react";
import { makeStyles } from "@material-ui/core/styles";
import clsx from "clsx";
import _ from "lodash";

import React from "react";

import { TransformationEventType } from "../types";
import {
  TransformedEvent,
  UntransformedEvent,
} from "../transformationDefinition";

const useStyles = makeStyles((theme) => ({
  beforeAndAfter: {
    display: "flex",
    justifyContent: "center",
  },
  transformBeforeOrAfter: {
    width: "50%",
    border: "1px solid var(--color-borderDefault)",
    fontFamily: '"Monospace", monospace',
  },
  transformBeforeOrAfterTitle: {
    fontWeight: "bold",
    backgroundColor: "var(--color-surface-muted)",
    borderBottom: "1px solid var(--color-borderDefault)",
    color: "var(--color-textSecondary)",
    width: "100%",
    paddingTop: theme.spacing(0.25),
    paddingBottom: theme.spacing(0.25),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    whiteSpace: "pre",
    textTransform: "uppercase",
    fontSize: "0.75rem",
  },
  transformBeforeOrAfterContent: {
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    overflowX: "scroll",
  },
  transformBeforeOrAfterLine: {
    whiteSpace: "pre-wrap",
    paddingTop: theme.spacing(0.25),
    paddingBottom: theme.spacing(0.25),
    paddingLeft: theme.spacing(1),
    paddingRight: theme.spacing(1),
    fontSize: "0.75rem",
    color: "var(--color-textDefault)",
  },
  beforeAndAfterDiff: {
    minWidth: 650,
    maxWidth: 1200,
    display: "flex",
  },
  transformBeforeOrAfterLineChanged: {
    backgroundColor: "var(--color-status-warning-tint)",
  },
  transformRemovedRow: {
    backgroundColor: "var(--color-status-error-tint)",
  },
  transformAddedRow: {
    backgroundColor: "var(--color-status-success-tint)",
  },
  key: {
    fontWeight: "bold",
  },
  removeIcon: {
    color: "var(--color-status-error)",
  },
  unlockIcon: {
    color: "var(--color-status-warning)",
  },
  lockIcon: {
    color: "var(--color-status-success)",
  },
}));

enum LineChangeType {
  Added = "ADDED",
  Modified = "MODIFIED",
  Removed = "REMOVED",
  Unchanged = "UNCHANGED",
}

const BeforeComparisonLine: React.FC<{
  indent: number;
  propKey: string;
  propValue: string | boolean | number;
  diffType: LineChangeType;
  editable?: boolean;
  iconSet: any;
}> = ({ indent, propKey, propValue, diffType, iconSet }) => {
  const classes = useStyles();

  let changeClass;
  switch (diffType) {
    case LineChangeType.Modified:
      changeClass = classes.transformBeforeOrAfterLineChanged;
      break;
    case LineChangeType.Removed:
      changeClass = classes.transformRemovedRow;
      break;
    default:
      // It doesn't make sense for Before components to be "Added" in the left
      // side of the diff. so LineChangeType.Added is not relevant to the Before
      // case
      break;
  }

  const renderedKey = (
    <span className={clsx(changeClass && clsx(classes.key))}>{propKey}</span>
  );
  const text = (
    <>
      {" ".repeat(indent * 2)}"{renderedKey}":{" "}
      <span>{JSON.stringify(propValue)}</span>,
    </>
  );
  const ModifiedIcon = iconSet[LineChangeType.Modified]?.before;
  const RemovedIcon = iconSet[LineChangeType.Removed];

  return (
    <div className={clsx(classes.transformBeforeOrAfterLine, changeClass)}>
      {diffType === LineChangeType.Modified && ModifiedIcon && (
        <ModifiedIcon className={clsx(classes.unlockIcon)} />
      )}
      {diffType === LineChangeType.Removed && RemovedIcon && (
        <RemovedIcon className={clsx(classes.removeIcon)} />
      )}
      {text}
    </div>
  );
};

// Icons used for security-related diffs
const securityIcons = {
  [LineChangeType.Added]: Plus,
  [LineChangeType.Removed]: Ban,
  [LineChangeType.Modified]: { before: LockOpen, after: Lock },
};

// Icons used for displaying Transformations
const xformIcons = {
  [LineChangeType.Added]: Plus,
  [LineChangeType.Removed]: Ban,
  [LineChangeType.Modified]: {
    before: FileDiff,
    after: FileDiff,
  },
};

export enum IconSetTypes {
  Security = "SECURITY",
  Transformations = "TRANSFORMATIONS",
}

const iconSets = {
  [IconSetTypes.Security]: securityIcons,
  [IconSetTypes.Transformations]: xformIcons,
};

const AfterComparisonLine: React.FC<{
  indent: number;
  propKey: string;
  propValue: string | boolean | number;
  keyChanged: boolean;
  valueChanged: boolean;
  diffType: LineChangeType;
  iconSet: any;
}> = ({
  indent,
  propKey,
  propValue,
  keyChanged,
  valueChanged,
  diffType,
  iconSet,
}) => {
  const classes = useStyles();
  const lineChanged = keyChanged || valueChanged;

  const renderedKey = (
    <span className={clsx(lineChanged && classes.key)}>{propKey}</span>
  );
  const renderedProp = <span>{JSON.stringify(propValue)}</span>;
  const ModifiedIcon = iconSet[LineChangeType.Modified]?.after;
  const AddedIcon = iconSet[LineChangeType.Added];

  return (
    <div
      className={clsx(
        classes.transformBeforeOrAfterLine,
        valueChanged && classes.transformAddedRow,
      )}
    >
      {diffType === LineChangeType.Modified && ModifiedIcon && (
        <ModifiedIcon className={clsx(classes.lockIcon)} />
      )}
      {diffType === LineChangeType.Added && AddedIcon && (
        <AddedIcon className={clsx(classes.lockIcon)} />
      )}
      {" ".repeat(indent * 2)}"{renderedKey}": {renderedProp},
    </div>
  );
};

function computeChangeType(before, after, propKey): LineChangeType {
  let isUnchanged = _.isEqual(
    before.eventProperties[propKey],
    after.eventProperties[propKey],
  );
  let isRenamed = after.renamedProperties[propKey];
  let isModified =
    before.eventProperties[propKey] &&
    after.eventProperties[propKey] &&
    !isUnchanged;
  let isRemoved = !after.eventProperties[propKey];
  let isAdded = after.eventProperties[propKey] && !isRenamed;

  let changeType;
  if (isUnchanged) {
    changeType = LineChangeType.Unchanged;
  } else if (isRemoved) {
    changeType = LineChangeType.Removed;
  } else if (isRenamed || isModified) {
    changeType = LineChangeType.Modified;
  } else if (isAdded) {
    changeType = LineChangeType.Added;
  } else {
    // Explicitly adding the default case where we cannot find a relevant row:
    // In this case, we'll default to Unchanged
    changeType = LineChangeType.Unchanged;
  }
  return changeType;
}

export const BeforeAndAfterComparison: React.FC<{
  before: UntransformedEvent;
  after: TransformedEvent;
  eventType: TransformationEventType;
  showEllipsis?: boolean;
  iconSet?: IconSetTypes;
}> = ({
  before,
  after,
  eventType,
  showEllipsis = true,
  iconSet = IconSetTypes.Transformations,
}) => {
  const classes = useStyles();

  const icons = iconSets[iconSet];

  const beforeRendered = _.map(
    before.eventProperties,
    (propValue: any, propKey: string) => {
      const indent = eventType !== "identify" ? 3 : 4;

      let changeType = computeChangeType(before, after, propKey);

      return (
        <BeforeComparisonLine
          key={propKey}
          indent={indent}
          editable={true}
          propKey={propKey}
          propValue={propValue}
          diffType={changeType}
          iconSet={icons}
        />
      );
    },
  );

  const afterRendered = _.map(
    after.eventProperties,
    (propValue: any, propKey: string) => {
      const keyBefore = _.findKey(
        after.renamedProperties,
        (v) => v === propKey,
      );
      const keyAfter = after.renamedProperties[propKey] || propKey;
      // @ts-ignore
      const valueBefore = before.eventProperties[keyBefore];
      const valueAfter = after.eventProperties[keyAfter];
      const keyChanged = keyBefore !== keyAfter;
      const valueChanged = !_.isEqual(valueAfter, valueBefore);
      const indent = eventType !== "identify" ? 3 : 4;

      let changeType = computeChangeType(before, after, propKey);

      return (
        <AfterComparisonLine
          key={propKey}
          indent={indent}
          propKey={propKey}
          propValue={propValue}
          keyChanged={keyChanged}
          valueChanged={valueChanged}
          diffType={changeType}
          iconSet={icons}
        />
      );
    },
  );

  if (eventType !== "identify") {
    return (
      <div className={classes.beforeAndAfterDiff}>
        <div className={classes.transformBeforeOrAfter}>
          <div className={classes.transformBeforeOrAfterTitle}>Before</div>
          <div className={classes.transformBeforeOrAfterContent}>
            <div className={classes.transformBeforeOrAfterLine}>{"{"}</div>
            <BeforeComparisonLine
              indent={1}
              propKey="event"
              propValue={before.eventName}
              diffType={
                before.eventName !== after.eventName
                  ? LineChangeType.Modified
                  : LineChangeType.Unchanged
              }
              iconSet={icons}
            />
            <div className={classes.transformBeforeOrAfterLine}>
              {'    "properties": {'}
            </div>
            {showEllipsis && (
              <div className={classes.transformBeforeOrAfterLine}>
                {"      ..."}
              </div>
            )}
            {beforeRendered}
            {showEllipsis && Object.keys(before.eventProperties).length > 1 && (
              <div className={classes.transformBeforeOrAfterLine}>
                {"      ..."}
              </div>
            )}
            <div className={classes.transformBeforeOrAfterLine}>{"    }"}</div>
            <div className={classes.transformBeforeOrAfterLine}>{"}"}</div>
          </div>
        </div>
        <div className={classes.transformBeforeOrAfter}>
          <div className={classes.transformBeforeOrAfterTitle}>After</div>
          <div className={classes.transformBeforeOrAfterContent}>
            <div className={classes.transformBeforeOrAfterLine}>{"{"}</div>
            <AfterComparisonLine
              indent={1}
              propKey="event"
              propValue={after.eventName}
              keyChanged={false}
              valueChanged={before.eventName !== after.eventName}
              diffType={
                before.eventName !== after.eventName
                  ? LineChangeType.Modified
                  : LineChangeType.Unchanged
              }
              iconSet={icons}
            />
            <div className={classes.transformBeforeOrAfterLine}>
              {'    "properties": {'}
            </div>
            {showEllipsis && (
              <div className={classes.transformBeforeOrAfterLine}>
                {"      ..."}
              </div>
            )}
            {afterRendered}
            {showEllipsis && Object.keys(after.eventProperties).length > 1 && (
              <div className={classes.transformBeforeOrAfterLine}>
                {"      ..."}
              </div>
            )}
            <div className={classes.transformBeforeOrAfterLine}>{"    }"}</div>
            <div className={classes.transformBeforeOrAfterLine}>{"}"}</div>
          </div>
        </div>
      </div>
    );
  } else {
    return (
      <div className={classes.beforeAndAfterDiff}>
        <div className={classes.transformBeforeOrAfter}>
          <div className={classes.transformBeforeOrAfterTitle}>Before</div>
          <div className={classes.transformBeforeOrAfterContent}>
            <div className={classes.transformBeforeOrAfterLine}>{"{"}</div>
            <BeforeComparisonLine
              indent={1}
              propKey="event"
              propValue={before.eventName}
              diffType={
                before.eventName !== after.eventName
                  ? LineChangeType.Modified
                  : LineChangeType.Unchanged
              }
              iconSet={icons}
            />
            <div className={classes.transformBeforeOrAfterLine}>
              {'    "properties": {'}
            </div>
            <div className={classes.transformBeforeOrAfterLine}>
              {'      "$user_props": {'}
            </div>
            {showEllipsis && (
              <div className={classes.transformBeforeOrAfterLine}>
                {"        ..."}
              </div>
            )}
            {beforeRendered}
            {showEllipsis && Object.keys(before.eventProperties).length > 1 && (
              <div className={classes.transformBeforeOrAfterLine}>
                {"        ..."}
              </div>
            )}
            <div className={classes.transformBeforeOrAfterLine}>
              {"      }"}
            </div>
            <div className={classes.transformBeforeOrAfterLine}>{"    }"}</div>
            <div className={classes.transformBeforeOrAfterLine}>{"}"}</div>
          </div>
        </div>
        <div className={classes.transformBeforeOrAfter}>
          <div className={classes.transformBeforeOrAfterTitle}>After</div>
          <div className={classes.transformBeforeOrAfterContent}>
            <div className={classes.transformBeforeOrAfterLine}>{"{"}</div>
            <AfterComparisonLine
              indent={1}
              propKey="event"
              propValue={after.eventName}
              keyChanged={false}
              valueChanged={before.eventName !== after.eventName}
              diffType={
                before.eventName !== after.eventName
                  ? LineChangeType.Modified
                  : LineChangeType.Unchanged
              }
              iconSet={icons}
            />
            <div className={classes.transformBeforeOrAfterLine}>
              {'    "properties": {'}
            </div>
            <div className={classes.transformBeforeOrAfterLine}>
              {'      "$user_props": {'}
            </div>
            {showEllipsis && (
              <div className={classes.transformBeforeOrAfterLine}>
                {"        ..."}
              </div>
            )}
            {afterRendered}
            {showEllipsis && Object.keys(after.eventProperties).length > 1 && (
              <div className={classes.transformBeforeOrAfterLine}>
                {"        ..."}
              </div>
            )}
            <div className={classes.transformBeforeOrAfterLine}>
              {"      }"}
            </div>
            <div className={classes.transformBeforeOrAfterLine}>{"    }"}</div>
            <div className={classes.transformBeforeOrAfterLine}>{"}"}</div>
          </div>
        </div>
      </div>
    );
  }
};
